import { useAlerts } from "@granular/gds";
import { trpc as coreQueryClient } from "../services/trpc";
import { useOperatingContext } from "./operatingContext";
import { useAuth } from "../services/auth/useAuth";
import { atomWithStorage } from "jotai/utils";
import { useAtom, useAtomValue } from "jotai";

export function useStartImpersonation() {
  const utils = coreQueryClient.useUtils();
  const auth = useAuth();
  const { resetLocalStoredContexts } = useOperatingContext();
  const preferTargetsFeatureFlags = useAtomValue(preferTargetsFeatureFlagsAtom);

  const {
    global: { danger },
  } = useAlerts();

  return coreQueryClient.auth.userImpersonationHandler.useMutation({
    onSuccess: async (data: { correlationId: string | null }) => {
      if (globalThis.Mobile?.startImpersonationV2) {
        // Android Code
        globalThis.Mobile?.startImpersonationV2(
          data.correlationId ?? "",
          preferTargetsFeatureFlags,
        );
      } else if (globalThis.Mobile?.startImpersonation) {
        // Android Code fallback
        globalThis.Mobile?.startImpersonation(data.correlationId ?? "");
      } else if (globalThis.webkit?.messageHandlers?.startImpersonationV2) {
        // iOS code — postMessage accepts a single argument, so we send a dict
        globalThis.webkit?.messageHandlers?.startImpersonationV2?.postMessage({
          correlationId: data.correlationId ?? "",
          preferTargetsFeatureFlags,
        });
      } else if (globalThis.webkit?.messageHandlers?.startImpersonation) {
        // iOS code fallback
        globalThis.webkit?.messageHandlers?.startImpersonation?.postMessage(
          data.correlationId ?? "",
        );
      }

      // In theory with spa SSO, we should _only_ need to set the correlation ID. MSAL would refetch on its own.
      if (auth.setCorrelationId && data.correlationId) {
        // If this method exists, it should handle refreshing as well.
        await auth.setCorrelationId(data.correlationId);
      } else {
        await auth.refreshAccessToken(data.correlationId ?? undefined);
      }

      // We need to invalidate everything because the user's context has changed.
      await utils.invalidate();
      resetLocalStoredContexts();

      //TODO: Fix the local storage issue to allow react state to notify changes so this isn't needed.
      globalThis.location.reload();
    },
    onError: (error) => {
      if (error?.data?.httpStatus === 404) {
        danger(error.message);
      }
    },
  });
}

export function useStopImpersonation() {
  const utils = coreQueryClient.useUtils();
  const auth = useAuth();
  const { resetLocalStoredContexts } = useOperatingContext();

  return coreQueryClient.auth.deleteUserImpersonationHandler.useMutation({
    onSuccess: async () => {
      // Native mobile code
      // Android Code
      globalThis.Mobile?.stopImpersonation("stop");

      // iOS code
      globalThis.webkit?.messageHandlers?.stopImpersonation?.postMessage(
        "stop",
      );

      if (auth.unsetCorrelationId) {
        // If this method exists, it should handle refreshing as well.
        await auth.unsetCorrelationId();
      } else {
        await auth.refreshAccessToken();
      }

      // We need to invalidate everything because the user's context has changed.
      await utils.invalidate();
      resetLocalStoredContexts();

      // Clear all filter-related storage before reload
      try {
        // Clear localStorage items related to filters and navigation
        const localStorageKeys = Object.keys(localStorage);
        const filterKeys = localStorageKeys.filter((key) =>
          key.includes("navMapSelectedFilterState"),
        );
        for (const key of filterKeys) {
          localStorage.removeItem(key);
        }

        // Clear sessionStorage as well
        const sessionStorageKeys = Object.keys(sessionStorage);
        const sessionFilterKeys = sessionStorageKeys.filter((key) =>
          key.includes("navMapSelectedFilterState"),
        );
        for (const key of sessionFilterKeys) {
          sessionStorage.removeItem(key);
        }
      } catch (error) {
        // Ignore storage errors in case of restricted environments
        console.warn(
          "Error clearing storage during impersonation stop:",
          error,
        );
      }

      //TODO: Fix the local storage issue to allow react state to notify changes so this isn't needed.
      globalThis.location.reload();
    },
  });
}

const preferTargetsFeatureFlagsAtom = atomWithStorage(
  "preferTargetsFeatureFlags",
  false,
);
/**
 * This hook just tracks some global state. This is set when deciding whether or not impersonation
 * should mimic feature flags, and it's read where the feature flags are interpreted.
 */
export function usePreferTargetsFeatureFlags(): [
  boolean,
  (newValue: boolean) => void,
] {
  const [preferTargetsFeatureFlags, setPreferTargetsFeatureFlags] = useAtom(
    preferTargetsFeatureFlagsAtom,
  );
  return [preferTargetsFeatureFlags, setPreferTargetsFeatureFlags];
}
